import { useApolloClient } from '@apollo/client'
import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { Input } from 'components/Input'
import { Label } from 'components/v2/Label'
import { Select } from 'components/v2/Select'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { Switch } from 'components/v2/Switch'
import FormLayout from 'features/App/components/FormLayout'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { resolvers as loginResolvers } from 'resolvers/login'
import { schema } from './schema'
import { getUnprocessableErrors } from 'utils/error'

const PRIORITIES = [1, 2, 3, 4, 5, 6, 7, 8]

const messages = defineMessages({
  companyPlaceholder: {
    id: 'companies.autocomplete.placeholder',
    defaultMessage: 'Select company'
  },
  namePlaceholder: {
    id: 'dictionary.name',
    defaultMessage: 'Name'
  },
  priorityPlaceholder: {
    id: 'bookingClasses.form.priority.placeholder',
    defaultMessage: 'Select priority'
  }
})

const NewBookingClassForm = (props: TSFixMe): TSFixMe => {
  const client = useApolloClient()
  client.addResolvers(loginResolvers)

  const { values, touched, errors, handleChange, setFieldValue, handleBlur, handleSubmit, error, loading } = props

  const intl = useIntl()

  const handleCompanyChange = (value: TSFixMe): TSFixMe => {
    setFieldValue('company', value)
  }

  const handleNameChange = ({ target: { value } }: TSFixMe): TSFixMe => {
    setFieldValue('name', value.toUpperCase())
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        <Row>
          <Column>
            <CompanyAutocomplete
              id="company.id"
              onChange={handleCompanyChange}
              onBlur={handleBlur}
              value={values.company.name}
              placeholder={intl.formatMessage(messages.companyPlaceholder)}
              touched={touched.company && touched.company.id}
              errors={errors.company && errors.company.id}
              label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
            />
          </Column>
        </Row>

        <Row>
          <Column>
            <Input
              id="name"
              onChange={handleNameChange}
              onBlur={handleBlur}
              value={values.name}
              placeholder={intl.formatMessage(messages.namePlaceholder)}
              touched={touched.name}
              errors={errors.name}
              label={<FormattedMessage id="dictionary.name" defaultMessage="Name" />}
            />
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            <Select
              id="priority"
              value={values.priority}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.priority}
              errors={errors.priority}
              label={<FormattedMessage id="dictionary.priority" defaultMessage="Priority" />}
            >
              <option value="">{intl.formatMessage(messages.priorityPlaceholder)}</option>

              {PRIORITIES.map((priority): TSFixMe => {
                return (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                )
              })}
            </Select>
          </Column>
        </Row>

        <Row>
          <Column width="50%">
            <Label>
              <FormattedMessage id="bookingClasses.new.form.promo.label" defaultMessage="Promo" />
            </Label>

            <Switch value={values.promo} onChange={(value): TSFixMe => setFieldValue('promo', value)} />
          </Column>
        </Row>
      </FormLayout.CardContent>

      <FormLayout.Split />

      <FormLayout.CardFooter>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="bookingClasses.form.error"
                defaultMessage="There was an error while creating your booking class"
              />
            </ErrorMessage>
          </div>
        )}

        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/booking-classes" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>

          <Button type="submit" kind="primary" loading={loading}>
            <FormattedMessage id="dictionary.create" defaultMessage="Create" />
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const NewBookingClass = ({ onSubmit, error, loading }: TSFixMe): TSFixMe => {
  const intl = useIntl()

  const initialBookingClass = {
    company: { id: '', name: '' },
    name: '',
    priority: '',
    promo: false
  }

  return (
    <Formik initialValues={initialBookingClass} validationSchema={schema(intl)} onSubmit={onSubmit}>
      {(props): TSFixMe => {
        return <NewBookingClassForm {...props} error={error} loading={loading} />
      }}
    </Formik>
  )
}
